import { useState, useCallback } from 'react';

export function useSessionStorage<T>(key: string, defaultValue: T) {
  const getValue = useCallback((): T => {
    const stored = sessionStorage.getItem(key);
    if (stored !== null) {
      try {
        return JSON.parse(stored) as T;
      } catch {
        console.error(`Error parsing sessionStorage key "${key}"`);
        return defaultValue;
      }
    }
    return defaultValue;
  }, [key, defaultValue]);

  const [storedValue, setStoredValue] = useState<T>(getValue);

  const setValue = useCallback((value: T) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
      setStoredValue(value);
    } catch (error) {
      console.error(`Error setting sessionStorage key "${key}":`, error);
    }
  }, [key]);

  const remove = useCallback(() => {
    sessionStorage.removeItem(key);
    setStoredValue(defaultValue);
  }, [key, defaultValue]);

  const clear = useCallback(() => {
    sessionStorage.clear();
    setStoredValue(defaultValue);
  }, [defaultValue]);

  return {
    value: storedValue, setValue, remove, clear,
  };
}
